import React, { useMemo } from "react";
import { Checkout, CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { billingAddress, getInfoByField, shippingAddress } from "@lib/utils/orderInfoUtility";
import { SummarySection } from "../SummarySection";
import { Divider } from "@ecco/ui/divider";
import { Flex } from "@ecco/ui/flex";
import { summaryStyles } from "../CheckoutStyles";
import { ShippingMethodSummary } from "./ShippingMethodSummary";
import { Box } from "@ecco/ui/box";
import type { CheckoutFormInitialValues } from "./types";
import { useLocale } from "next-intl";
import { getPrivacyAttributes } from "@lib/monitoring/privacyAttributes";

type Props = {
    values: CheckoutFormInitialValues;
};

export const DeliveryStepSummary = ({ values }: Props) => {
    const { getMultiple } = useMicrocopy();
    const locale = useLocale();
    const microcopies = getMultiple(CommonCMS.page, [
        Checkout.step2.billingAddress,
        Checkout.step2.shippingAddress,
    ]);
    const headers = {
        billingAddress: microcopies[Checkout.step2.billingAddress],
        shippingAddress: microcopies[Checkout.step2.shippingAddress],
    };

    const shippingAddressSpec = shippingAddress(
        headers.shippingAddress,
        values.deliveryAddress?.country ?? ""
    );
    const billingAddressSpec = billingAddress(
        headers.billingAddress,
        values.billingAddress?.country ?? ""
    );

    const renderShippingAddress = useMemo(() => {
        const info = getInfoByField(shippingAddressSpec, values, locale);

        return <SummarySection content={info.value} headline={shippingAddressSpec.name} />;
    }, [shippingAddressSpec, values]);

    const renderBillingAddress = useMemo(() => {
        const info = getInfoByField(billingAddressSpec, values, locale);

        return <SummarySection content={info.value} headline={billingAddressSpec.name} />;
    }, [billingAddressSpec, values]);

    return (
        <Flex
            flexDirection="column"
            gap="6"
            alignItems="stretch"
            justifyContent="flex-start"
            {...getPrivacyAttributes()}
        >
            <ShippingMethodSummary methodId={values.deliveryMethodId} />
            <Divider />
            <Flex flexDirection={{ base: "column", md: "row" }} gap="6">
                <Box {...summaryStyles.column}>{renderShippingAddress}</Box>
                <Box {...summaryStyles.column}>
                    {renderBillingAddress}
                    {values.vatNumber && <SummarySection content={values.vatNumber} />}
                </Box>
            </Flex>
        </Flex>
    );
};
